import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";

import { useApi } from "../../lib/useApi";
import { clearManualImages } from "./imageResources";

export function useManualOwner() {
  const auth = useAuth();
  return [auth.user?.profile.iss ?? "", auth.user?.profile.sub ?? ""] as const;
}

/** 見える範囲を決めるロールだけで世代を分ける。Bearer 自体はクエリキーに残さない。
 * iat/exp/jti まで入れると、トークンの更新のたびに索引が捨てられ、取り直しの間ヘッダの
 * ボタンが現在の画面の節を引けなくなる(同じ主体・同じロールなら見える範囲は同じ)。 */
export function useManualGeneration() {
  const auth = useAuth();
  try {
    const payload = auth.user?.access_token.split(".")[1];
    if (!payload) return "";
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as {
      roles?: string[];
    };
    return JSON.stringify([...(claims.roles ?? [])].sort());
  } catch {
    return "";
  }
}

export function useManualIndex() {
  const api = useApi();
  const auth = useAuth();
  const [iss, sub] = useManualOwner();
  const generation = useManualGeneration();
  // 索引を初めて読む時点では view をまだ知らない。本文・画像は取得した view で分ける。
  return useQuery({
    queryKey: ["manual-index", iss, sub, generation],
    queryFn: ({ signal }) => api.manualIndex(signal),
    enabled: auth.isAuthenticated,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/** 外殻に 1 回置く。主体・トークンが変わったら古い要求とキャッシュを捨てる。 */
export function useManualSession(view?: string) {
  const auth = useAuth();
  const client = useQueryClient();
  const [iss, sub] = useManualOwner();
  const generation = useManualGeneration();
  const activeOwner = useRef<string | null>(null);
  const previousView = useRef<{ owner: string; view?: string } | undefined>(undefined);
  useEffect(() => {
    const owner = JSON.stringify([iss, sub, generation]);
    const old = previousView.current;
    if (old?.owner === owner && old.view && view && old.view !== view) {
      const filters = { queryKey: ["manual", iss, sub, old.view] };
      void client.cancelQueries(filters).then(() => client.removeQueries(filters));
      clearManualImages(owner, old.view);
    }
    if (view) previousView.current = { owner, view };
  }, [client, generation, iss, sub, view]);
  useEffect(() => {
    const owner = JSON.stringify([iss, sub, generation]);
    activeOwner.current = owner;
    const discard = () => {
      const filters = { predicate: (q: { queryKey: readonly unknown[] }) =>
        (q.queryKey[0] === "manual" || q.queryKey[0] === "manual-index") &&
        q.queryKey[1] === iss && q.queryKey[2] === sub &&
        q.queryKey[q.queryKey.length - 1] === generation };
      void client.cancelQueries(filters).then(() => client.removeQueries(filters));
      clearManualImages(owner);
    };
    window.addEventListener("itmgmt-signout", discard);
    const removeUnloaded = auth.events.addUserUnloaded(discard);
    const removeSignedOut = auth.events.addUserSignedOut(discard);
    return () => {
      window.removeEventListener("itmgmt-signout", discard);
      removeUnloaded();
      removeSignedOut();
      activeOwner.current = null;
      // StrictMode の同じ主体での再マウントは新しい要求を消さない。
      queueMicrotask(() => { if (activeOwner.current !== owner) discard(); });
    };
  }, [auth.events, client, generation, iss, sub]);
}
